/**
 * On-screen Organogram — the third chart view on /organization.
 *
 * Renders the SAME paged geometry the PDF export uses (POST
 * /org/organogram/geometry), one page at a time as SVG on a white "paper"
 * card, styled to the platform (heritage-blue boxes, amber vacancies, red
 * Critical Role) rather than the official pack's print palette.
 *
 * Interactions mirror the existing org views:
 *  - click a seat box → the PersonDrawer (same transfer / reporting-line
 *    actions), click a role box (structure mode) → the RoleDetailDrawer;
 *  - click a "→ part N" marker → jump to that page;
 *  - hierarchy / page-by / facet filters like the export dialog;
 *  - zoom / fit / drag-pan; Print (current page, A4 landscape); Export PDF.
 *
 * Refetches in place on `reloadKey` bumps (after drawer mutations), same as
 * the trees.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { useRmTaxonomyOptions } from '@/hooks/useRmTaxonomyOptions';
import {
  organizationService,
  type OrganogramGeometry, type OrganogramPage, type OrganogramBox,
} from '@/services/api/organizationService';
import {
  AlertTriangle, Briefcase, ChevronLeft, ChevronRight, FileDown, Loader2,
  Minus, Plus, Printer, RefreshCw, Scan,
} from 'lucide-react';

// Mirrors the engine's _wrap (24-char greedy) so screen boxes break text the
// same way the PDF does — the engine measured box heights with this wrapping.
function wrapText(text: string, maxChars = 24): string[] {
  const words = (text || '').split(/\s+/).filter(Boolean);
  const out: string[] = [];
  let cur = '';
  for (let w of words) {
    while (w.length > maxChars) {
      if (cur) { out.push(cur); cur = ''; }
      out.push(w.slice(0, maxChars - 1) + '-');
      w = w.slice(maxChars - 1);
    }
    if (!cur) cur = w;
    else if (cur.length + 1 + w.length <= maxChars) cur += ' ' + w;
    else { out.push(cur); cur = w; }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
}

const LINE_H = 9.5;
const PAD_Y = 6;

const STYLE_FILL: Record<string, string> = {
  title: '#111827',
  person: '#6B7280',
  flag_critical: '#DC2626',
  flag_vacant: '#B45309',
  meta: '#6B7280',
};

export interface OrganogramViewState {
  hierarchy: 'reporting' | 'structure' | 'roles';
  pageBy: 'function' | 'department' | 'campus' | 'none';
  fn: string;
  hrDept: string;
  team: string;
  gradeFrom: string;
  gradeTo: string;
  campus: string;
  dimCampus: string;
  dimDept: string;
  dimFaculty: string;
  workerTypes: string[];
  positionTypes: string[];
}

interface Props {
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  onOpenRole?: (entryId: number, roleCode: string) => void;
  onExportPdf?: (state: OrganogramViewState) => void;
  campusOptions?: string[];
  dimCampusOptions?: string[];
  dimDeptOptions?: string[];
  dimFacultyOptions?: string[];
  workerTypeOptions?: string[];
  positionTypeOptions?: string[];
  reloadKey?: number;
}

export const OrganogramView: React.FC<Props> = ({
  onOpenPerson, onOpenRole, onExportPdf,
  campusOptions = [], dimCampusOptions = [], dimDeptOptions = [],
  dimFacultyOptions = [], workerTypeOptions = [], positionTypeOptions = [],
  reloadKey = 0,
}) => {
  const [hierarchy, setHierarchy] = useState<'reporting' | 'structure' | 'roles'>('reporting');
  const [pageBy, setPageBy] = useState<'function' | 'department' | 'campus' | 'none'>('function');
  const [fn, setFn] = useState('all');
  const [hrDept, setHrDept] = useState('all');
  const [team, setTeam] = useState('all');
  const [gradeFrom, setGradeFrom] = useState('all');   // most-senior end (e.g. F1)
  const [gradeTo, setGradeTo] = useState('all');       // most-junior end (e.g. B1)
  const [campus, setCampus] = useState('all');
  const [dimCampus, setDimCampus] = useState('all');
  const [dimDept, setDimDept] = useState('all');
  const [dimFaculty, setDimFaculty] = useState('all');
  const [workerTypes, setWorkerTypes] = useState<Set<string>>(() => new Set());
  const [positionTypes, setPositionTypes] = useState<Set<string>>(() => new Set());
  const taxonomy = useRmTaxonomyOptions();

  // "From F to B": an inclusive slice of the seniority-ordered grade list. Either
  // end may stay open ("all"), so From-only = "F and below", To-only = "down to B".
  const gradeSlice = useMemo(() => {
    const all = taxonomy.grades;
    if (!all.length || (gradeFrom === 'all' && gradeTo === 'all')) return [];
    const i = gradeFrom === 'all' ? 0 : all.indexOf(gradeFrom);
    const j = gradeTo === 'all' ? all.length - 1 : all.indexOf(gradeTo);
    if (i < 0 || j < 0) return [];
    const [lo, hi] = i <= j ? [i, j] : [j, i];   // tolerate reversed picks
    return all.slice(lo, hi + 1);
  }, [taxonomy.grades, gradeFrom, gradeTo]);

  const [data, setData] = useState<OrganogramGeometry | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [zoom, setZoom] = useState(1);          // 1 = fit-width of the paper card
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const movedRef = useRef(0);          // px moved during the current drag — suppresses the trailing click
  const reqSeq = useRef(0);            // stale-response guard for load()
  const svgWrapRef = useRef<HTMLDivElement>(null);
  const didMount = useRef(false);

  const load = useCallback(async (silent = false) => {
    const seq = ++reqSeq.current;
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const g = await organizationService.organogramGeometry({
        hierarchy,
        page_by: pageBy,
        filters: {
          function: fn === 'all' ? null : fn,
          hr_department: hrDept === 'all' ? null : hrDept,
          team: team === 'all' ? null : team,
          campus: campus === 'all' ? null : campus,
          dimension_campus: dimCampus === 'all' ? null : dimCampus,
          dimension_department: dimDept === 'all' ? null : dimDept,
          dimension_faculty: dimFaculty === 'all' ? null : dimFaculty,
          grades: gradeSlice,
          worker_types: Array.from(workerTypes),
          position_types: Array.from(positionTypes),
          include_vacant: true,
        },
      });
      if (seq !== reqSeq.current) return;   // a newer request superseded this one
      setData(g);
      setPageIdx((i) => Math.min(i, Math.max(0, g.pages.length - 1)));
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } catch (e: any) {
      if (seq !== reqSeq.current) return;
      setError(e?.response?.data?.detail || e?.message || 'Failed to load the organogram');
    } finally {
      if (seq === reqSeq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [hierarchy, pageBy, fn, hrDept, team, gradeSlice, campus, dimCampus, dimDept, dimFaculty, workerTypes, positionTypes]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    load(true); // silent in-place refresh after a mutation
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reset the viewport when the page changes.
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [pageIdx, hierarchy, pageBy]);

  const page: OrganogramPage | null = data?.pages[pageIdx] ?? null;

  const toggleIn = (set: React.Dispatch<React.SetStateAction<Set<string>>>) => (v: string) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });

  const jumpToPart = useCallback((partLabel: string) => {
    if (!data || !page) return;
    // "part 3" within the current group: group = title without any "(part N)".
    const group = page.title.replace(/ \(part \d+\)$/, '');
    const target = partLabel === 'part 1'
      ? group
      : `${group} (${partLabel})`;
    const idx = data.pages.findIndex((p) => p.title === target);
    if (idx >= 0) setPageIdx(idx);
  }, [data, page]);

  // A marker box is usually ALSO a real seat: its body opens the drawer and the
  // "→ part N" line (its own click target) jumps. Body-click only falls back to
  // jumping when the id parses to nothing drawer-openable.
  const boxAction = useCallback((b: OrganogramBox): 'person' | 'role' | 'jump' | null => {
    const id = b.id.replace(/^(ghost:)+/, '');
    if (/^(?:pos|ghost)_\d+$/.test(id) && onOpenPerson) return 'person';
    if (/^role_\d+(?:~.+)?$/.test(id) && onOpenRole) return 'role';
    if (b.continues_to) return 'jump';
    return null;
  }, [onOpenPerson, onOpenRole]);

  const handleBoxClick = useCallback((b: OrganogramBox) => {
    if (movedRef.current > 4) return;   // that click ended a pan, not a pick
    const id = b.id.replace(/^(ghost:)+/, '');
    const action = boxAction(b);
    if (action === 'person') { onOpenPerson!(null, Number(id.match(/_(\d+)$/)![1])); return; }
    if (action === 'role') { onOpenRole!(Number(id.match(/^role_(\d+)/)![1]), ''); return; }
    if (action === 'jump' && b.continues_to) jumpToPart(b.continues_to);
  }, [boxAction, jumpToPart, onOpenPerson, onOpenRole]);

  // ── Print: a standalone document with just this page's SVG, A4 landscape ──
  const handlePrint = useCallback(() => {
    const svg = svgWrapRef.current?.querySelector('svg');
    if (!svg || !page) return;
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const win = window.open('', '_blank', 'width=1100,height=800');
    if (!win) {
      toast.error('The print window was blocked — allow popups for this site to print.');
      return;
    }
    // Fit BOTH dimensions of the A4-landscape printable area (~277×182mm after
    // margins + heading) — width-only sizing slices tall pages across sheets.
    const aspect = (page.content_h + 20) / Math.max(1, page.content_w + 20);
    const svgCss = aspect > 182 / 277
      ? 'svg { height: 182mm; width: auto; display: block; margin: 0 auto; }'
      : 'svg { width: 100%; height: auto; }';
    win.document.write(`<!doctype html><html><head><title>${esc(page.title)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { margin: 0; font-family: Helvetica, Arial, sans-serif; }
  h1 { color: #2563EB; font-size: 16px; text-align: center; margin: 4px 0 2px; }
  p  { color: #6B7280; font-size: 9px; text-align: center; margin: 0 0 8px; }
  ${svgCss}
</style></head><body>
<h1>Eduvos Organogram – ${esc(page.title)}</h1>
<p>${page.subtitle ? esc(page.subtitle) + ' · ' : ''}Generated from the live platform · ${new Date(data?.generated_at || Date.now()).toLocaleString()}</p>
${svg.outerHTML}
</body></html>`);
    win.document.close();
    win.focus();
    win.onafterprint = () => win.close();
    setTimeout(() => win.print(), 300);
  }, [page, data]);

  const renderBox = (b: OrganogramBox) => {
    const fill = b.ghost ? '#F3F4F6' : b.vacant && !b.rollup ? '#FEF3C7' : '#FFFFFF';
    const stroke = b.ghost ? '#9CA3AF' : '#1D4ED8';
    let ty = b.y + PAD_Y + LINE_H * 0.75;
    const texts: React.ReactNode[] = [];
    for (const ln of b.lines) {
      const isTitle = ln.style === 'title';
      const isFlag = ln.style.startsWith('flag');
      for (const piece of wrapText(ln.text)) {
        texts.push(
          <text
            key={`${b.id}-${ty}`}
            x={b.x + b.w / 2}
            y={ty}
            textAnchor="middle"
            fontSize={7}
            fontWeight={isFlag ? 700 : isTitle ? 600 : 400}
            fontStyle={ln.style === 'flag_vacant' ? 'italic' : undefined}
            fill={STYLE_FILL[ln.style] ?? STYLE_FILL.title}
          >
            {piece}
          </text>,
        );
        ty += LINE_H;
      }
    }
    if (b.rollup) {
      texts.push(
        <text key={`${b.id}-rollup`} x={b.x + b.w / 2} y={ty} textAnchor="middle"
              fontSize={6.5} fill={STYLE_FILL.meta}>{b.rollup}</text>,
      );
      ty += LINE_H;
    }
    if (b.continues_to) {
      const cont = b.continues_to;
      texts.push(
        <text key={`${b.id}-cont`} x={b.x + b.w / 2} y={ty} textAnchor="middle"
              fontSize={6.5} fontStyle="italic" fill="#2563EB" textDecoration="underline"
              className="cursor-pointer"
              onClick={(ev) => { ev.stopPropagation(); if (movedRef.current <= 4) jumpToPart(cont); }}>
          {`→ ${cont}`}
        </text>,
      );
    }
    const action = boxAction(b);
    return (
      <g
        key={b.id}
        onClick={action ? () => handleBoxClick(b) : undefined}
        onKeyDown={action ? (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); handleBoxClick(b); }
        } : undefined}
        className={action ? 'cursor-pointer transition-opacity hover:opacity-75' : undefined}
        role={action ? 'button' : undefined}
        tabIndex={action ? 0 : undefined}
        aria-label={b.lines[0]?.text}
      >
        <rect
          x={b.x} y={b.y} width={b.w} height={b.h}
          fill={fill} stroke={stroke} strokeWidth={b.ghost ? 0.7 : 1}
          strokeDasharray={b.ghost ? '3 2' : undefined} rx={2}
        />
        {b.accent && <rect x={b.x} y={b.y} width={2.5} height={b.h} fill={b.accent} rx={1} />}
        {texts}
      </g>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {/* ── Control bar ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={hierarchy} onValueChange={(v) => { setHierarchy(v as any); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="reporting">Reporting lines (people)</SelectItem>
            <SelectItem value="roles">Roles by reporting line</SelectItem>
            <SelectItem value="structure">Structure (roles)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pageBy} onValueChange={(v) => { setPageBy(v as any); setPageIdx(0); }}
                disabled={hierarchy === 'structure'}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="function">Page per function</SelectItem>
            <SelectItem value="department">Page per department</SelectItem>
            <SelectItem value="campus">Page per campus</SelectItem>
            <SelectItem value="none">Single chart (no grouping)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fn} onValueChange={(v) => { setFn(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Function" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All functions</SelectItem>
            {taxonomy.functions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={hrDept} onValueChange={(v) => { setHrDept(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[170px]"><SelectValue placeholder="HR department" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All HR departments</SelectItem>
            {taxonomy.hrDepartments.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={team} onValueChange={(v) => { setTeam(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All teams</SelectItem>
            {taxonomy.teams.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={gradeFrom} onValueChange={(v) => { setGradeFrom(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[120px]"><SelectValue placeholder="Grade from" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Grade: top</SelectItem>
            {taxonomy.grades.map((g) => <SelectItem key={g} value={g}>From {g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={gradeTo} onValueChange={(v) => { setGradeTo(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[120px]"><SelectValue placeholder="Grade to" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Grade: bottom</SelectItem>
            {taxonomy.grades.map((g) => <SelectItem key={g} value={g}>To {g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={campus} onValueChange={(v) => { setCampus(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {campusOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dimCampus} onValueChange={(v) => { setDimCampus(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Fin. campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. campuses</SelectItem>
            {dimCampusOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dimDept} onValueChange={(v) => { setDimDept(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Fin. department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. departments</SelectItem>
            {dimDeptOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dimFaculty} onValueChange={(v) => { setDimFaculty(v); setPageIdx(0); }}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Faculty" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All faculties</SelectItem>
            {dimFacultyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', (workerTypes.size + positionTypes.size) > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Types
              {(workerTypes.size + positionTypes.size) > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{workerTypes.size + positionTypes.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 w-60 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Worker type (filled seats)</DropdownMenuLabel>
            {workerTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem key={`w-${e}`} checked={workerTypes.has(e)}
                onCheckedChange={() => { toggleIn(setWorkerTypes)(e); setPageIdx(0); }}
                onSelect={(ev) => ev.preventDefault()}>
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Position type (incl. vacant)</DropdownMenuLabel>
            {positionTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem key={`p-${e}`} checked={positionTypes.has(e)}
                onCheckedChange={() => { toggleIn(setPositionTypes)(e); setPageIdx(0); }}
                onSelect={(ev) => ev.preventDefault()}>
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-9" onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))} aria-label="Zoom out">
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} aria-label="Fit">
            <Scan className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setZoom((z) => Math.min(4, z + 0.2))} aria-label="Zoom in">
            <Plus className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={handlePrint} disabled={!page}>
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print page
          </Button>
          {onExportPdf && (
            <Button variant="outline" size="sm" className="h-9"
              onClick={() => onExportPdf({
                hierarchy, pageBy, fn, hrDept, team, gradeFrom, gradeTo,
                campus, dimCampus, dimDept, dimFaculty,
                workerTypes: Array.from(workerTypes), positionTypes: Array.from(positionTypes),
              })}>
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> Export PDF
            </Button>
          )}
        </div>
      </div>

      {/* ── Page bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-8" disabled={pageIdx <= 0}
                onClick={() => setPageIdx((i) => i - 1)} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Select value={String(pageIdx)} onValueChange={(v) => setPageIdx(Number(v))}>
          <SelectTrigger className="h-8 w-[340px]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">
            {(data?.pages ?? []).map((p, i) => (
              <SelectItem key={i} value={String(i)}>{p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8"
                disabled={!data || pageIdx >= data.pages.length - 1}
                onClick={() => setPageIdx((i) => i + 1)} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="text-xs text-muted-foreground">
          {data ? `page ${Math.min(pageIdx + 1, data.pages.length)} of ${data.pages.length} · ${data.total_positions.toLocaleString()} seats` : ''}
          {page?.subtitle ? ` · ${page.subtitle}` : ''}
        </span>
        {refreshing && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3 animate-spin" /> Refreshing…
          </span>
        )}
      </div>

      {/* ── Paper ────────────────────────────────────────────────────────── */}
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-lg border border-border bg-muted/40">
        {loading ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Building the organogram…
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <p className="text-sm text-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={() => load()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Retry
            </Button>
          </div>
        ) : !page ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No positions matched the selected filters.
          </div>
        ) : (
          <div
            ref={svgWrapRef}
            className="h-full w-full cursor-grab select-none overflow-hidden p-4 active:cursor-grabbing"
            onMouseDown={(e) => {
              e.preventDefault();   // stop native text-selection dragging over labels
              movedRef.current = 0;
              dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
            }}
            onMouseMove={(e) => {
              const d = dragRef.current;
              if (!d) return;
              const dx = e.clientX - d.x;
              const dy = e.clientY - d.y;
              movedRef.current = Math.abs(dx) + Math.abs(dy);
              setPan({ x: d.px + dx, y: d.py + dy });
            }}
            onMouseUp={() => { dragRef.current = null; }}
            onMouseLeave={() => { dragRef.current = null; }}
          >
            <div
              className="mx-auto h-full max-w-full rounded bg-white shadow-md dark:shadow-black/40"
              style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'top center' }}
            >
              <svg
                viewBox={`-10 -10 ${page.content_w + 20} ${page.content_h + 20}`}
                className="h-full w-full"
                preserveAspectRatio="xMidYMid meet"
              >
                <g stroke="#1D4ED8" strokeWidth={0.8} fill="none">
                  {page.edges.map((e, i) => (
                    <polyline key={i} points={e.points.map(([x, y]) => `${x},${y}`).join(' ')} />
                  ))}
                </g>
                {page.boxes.map(renderBox)}
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
